import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  Grid,
  CircularProgress,
  FormHelperText,
  Avatar,
  AvatarGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Visibility, VisibilityOff, Check, Close } from "@mui/icons-material";
import { Link, NavLink, useNavigate, useParams } from "react-router-dom";
import finalLogo from "../../assets/images/logo.png";
import LeftSideImage from "../../assets/images/reset-password.png";
import { toast } from "react-toastify";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { setPassword, getAdminSettingsFlag } from "./services";

const SetPassword = ({ token: propToken }) => {
  const { token: paramToken } = useParams();
  // Use prop token if available, otherwise fall back to URL param
  const token = propToken || paramToken;
  
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requireStrongPassword, setRequireStrongPassword] = useState(false);
  const navigate = useNavigate();

  // Fetch admin settings on component mount
  useEffect(() => {
    const fetchAdminSettings = async () => {
      try {
        const response = await getAdminSettingsFlag();
        if (response.data && response.data.payload.require_strong_passwords) {
          setRequireStrongPassword(true);
        }
      } catch (error) {
        console.error("Error fetching admin settings:", error);
        // Continue with default behavior if API fails
      }
    };
    
    fetchAdminSettings();
  }, []);

  // Password strength validation functions
  const getPasswordValidation = (password) => {
    return {
      length: password.length >= 8 && password.length <= 20,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      noSpaces: !/\s/.test(password)
    };
  };

  const passwordValidation = getPasswordValidation(newPassword);

  // Effect to handle password confirmation in real-time
  useEffect(() => {
    // Check password confirmation
    if (newPassword && confirmPassword && newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match.");
    } else {
      setPasswordError("");
    }
  }, [newPassword, confirmPassword]);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  const isPasswordValid = () => {
    if (!requireStrongPassword) {
      return newPassword.length >= 6;
    }
    return Object.values(passwordValidation).every(Boolean);
  };

  const handleSubmit = async () => {
    // Client-side validation before API call
    if (!newPassword || !confirmPassword) {
      toast.error("Please fill in both password fields.", {
        toastId: "setPassword-validation"
      });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match.", {
        toastId: "setPassword-validation"
      });
      return;
    }
    
    if (!isPasswordValid()) {
      toast.error("Please meet all password requirements.", {
        toastId: "setPassword-validation"
      });
      return;
    }

    setLoading(true);
    try {
      const response = await setPassword({
        password: newPassword,
      }, token);

      if (response.status === 200 || response.data.success) {
        toast.success(response.data.message || "Password set successfully!", {
          toastId: "setPassword-success"
        });
        navigate("/login");
      } else {
        toast.error(response.data.message || "Failed to set new password.", {
          toastId: "setPassword-error"
        });
      }
    } catch (error) {
      console.error("Error setting password:", error);
      toast.error(
        error.response?.data?.message || "An error occurred. Please try again.",
        { toastId: "setPassword-error" }
      );
    } finally {
      setLoading(false);
    }
  };

  const ValidationItem = ({ isValid, text }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
      {isValid ? (
        <Check sx={{ fontSize: 16, color: '#4CAF50', mr: 1 }} />
      ) : (
        <Close sx={{ fontSize: 16, color: '#F44336', mr: 1 }} />
      )}
      <Typography
        variant="caption"
        sx={{
          fontSize: '12px',
          color: isValid ? '#4CAF50' : '#F44336',
          fontWeight: 500
        }}
      >
        {text}
      </Typography>
    </Box>
  );

  return (
    <Grid
      container
      minHeight="100vh"
      bgcolor="#f5faff"
      style={{ textAlign: "center" }}
    >
      <Grid
        item
        xs={false}
        md={6}
        sx={{
          display: { xs: "none", md: "flex" },
          alignItems: "center",
          justifyContent: "center",
          p: 4,
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        <img src={LeftSideImage} alt="Set Password Illustration" />
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
        sx={{
          display: "flex",
          flexDirection: "column",
          p: { xs: 2, sm: 4 },
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        <Box
          onClick={() => navigate("/login")}
          sx={{
            cursor: "pointer",
            backgroundColor: "#1570EF",
            color: "white",
            borderRadius: "10px",
            width: 32,
            height: 32,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2,
            mt: 2,
            ml: 1,
            alignSelf: "flex-start",
          }}
        >
          <ArrowBackIosIcon sx={{ fontSize: 16 }} />
        </Box>
        
        <Box
          sx={{
            justifyContent: "center",
            mb: 1,
            mt: 3,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          <Box
            onClick={() => navigate("/")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              textAlign: "center",
            }}
          >
            <img
              src={finalLogo}
              alt="Logo"
              style={{ width: "250px", cursor: "pointer" }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            width: "100%",
            maxWidth: 400,
            mt: 4,
            backgroundColor: "#FFFFFF",
            padding: { xs: "20px", sm: "25px", md: "30px" },
            borderRadius: "8px",
            mx: "auto",
            boxShadow: "rgba(100, 100, 111, 0.2) 0px 7px 29px 0px",
          }}
        >
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: "bold", 
              mb: 4, 
              mt: 3,
              fontSize: { xs: "1.5rem", sm: "1.75rem" }
            }}
          >
            Set Password
          </Typography>
          
          <Typography
            variant="body2"
            sx={{
              fontWeight: "600",
              textAlign: "left",
              color: "#333333",
              mb: 1,
              fontSize: { xs: "14px", sm: "16px" }
            }}
          >
            Password
          </Typography>
          
          <TextField
            fullWidth
            placeholder="Enter new password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            type={showPassword ? "text" : "password"}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    sx={{ color: "#1976d2" }}
                    onClick={togglePasswordVisibility}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                boxShadow: "rgba(149, 157, 165, 0.2) 0px 8px 24px",
                borderRadius: "8px",
              },
              "& .MuiOutlinedInput-input": {
                padding: { xs: "12px", sm: "14px" },
                fontSize: { xs: "14px", sm: "16px" },
                "&::placeholder": {
                  fontSize: { xs: "14px", sm: "16px" },
                },
              },
            }}
          />
          
          <Typography
            variant="body2"
            sx={{
              fontWeight: "600",
              textAlign: "left",
              color: "#333333",
              mb: 1,
              fontSize: { xs: "14px", sm: "16px" }
            }}
          >
            Confirm Password
          </Typography>
          
          <TextField
            fullWidth
            placeholder="Confirm your password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            type={showConfirmPassword ? "text" : "password"}
            error={!!passwordError}
            helperText={passwordError}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    sx={{ color: "#1976d2" }}
                    onClick={toggleConfirmPasswordVisibility}
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              mb: requireStrongPassword ? 2 : 3,
              "& .MuiOutlinedInput-root": {
                boxShadow: "rgba(149, 157, 165, 0.2) 0px 8px 24px",
                borderRadius: "8px",
              },
              "& .MuiOutlinedInput-input": {
                padding: { xs: "12px", sm: "14px" },
                fontSize: { xs: "14px", sm: "16px" },
                "&::placeholder": {
                  fontSize: { xs: "14px", sm: "16px" },
                },
              },
            }}
          />

          {requireStrongPassword && newPassword && (
            <Box sx={{ mb: 3, textAlign: 'left' }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 1, 
                  fontWeight: 600,
                  fontSize: { xs: "13px", sm: "14px" }
                }}
              >
                Password must include:
              </Typography>
              <ValidationItem 
                isValid={passwordValidation.length} 
                text="8-20 Characters" 
              />
              <ValidationItem 
                isValid={passwordValidation.uppercase} 
                text="At least one capital letter" 
              />
              <ValidationItem 
                isValid={passwordValidation.lowercase} 
                text="At least one lowercase letter" 
              />
              <ValidationItem 
                isValid={passwordValidation.number} 
                text="At least one number" 
              />
              <ValidationItem 
                isValid={passwordValidation.specialChar} 
                text="At least one special character" 
              />
              <ValidationItem 
                isValid={passwordValidation.noSpaces} 
                text="No spaces" 
              />
            </Box>
          )}

          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !isPasswordValid()}
            sx={{
              bgcolor: "#1570EF",
              color: "white",
              textTransform: "capitalize",
              fontSize: { xs: "14px", sm: "16px" },
              borderRadius: "6px",
              py: { xs: 1.2, sm: 1.5 },
              mt: 2,
              "&:hover": { bgcolor: "#1565c0" },
              "&:disabled": {
                bgcolor: "#ccc",
                color: "#666",
              },
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Set Password"
            )}
          </Button>
        </Box>
        
        <Box className="pt-4" sx={{ textAlign: "left", mt: 3 }}>
          <AvatarGroup max={4} style={{ justifyContent: "flex-end" }}>
            <Avatar alt="User 1" src="https://i.pravatar.cc/150?img=1" />
            <Avatar alt="User 2" src="https://i.pravatar.cc/150?img=2" />
            <Avatar alt="User 3" src="https://i.pravatar.cc/150?img=3" />
            <Avatar alt="User 4" src="https://i.pravatar.cc/150?img=4" />
          </AvatarGroup>
          <Typography 
            variant="body2" 
            color="text.secondary" 
            className="pt-2"
            sx={{ fontSize: { xs: "12px", sm: "14px" } }}
          >
            "Recruiters at Amazon, Wise, & Other <br /> Major Players Are
            Reaching Out Directly!"
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
};

export default SetPassword;
